import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useFont } from "../../../contexts/FontContext/FontContext";
import { useLogo } from "../../../contexts/LogoContext/LogoContext";
import ExpiredNotice from "../ExpiredNotice.js";
import { useLocation, useNavigate } from "react-router-dom";
import LogoWrapper from "../../LogoWrapper/LogoWrapper";
import styles from "./ExchangeLink.module.css";
import { fetchReturnData, startEllipsisInterval } from "../../utils/common";
import ItemExchange from "./ItemExchange";
import useExpiration from "../../../hooks/useExpiration";
import usePortalSession from "../../../hooks/usePortalSession";
import ExchangeItemSelect from "./ExchangeItemSelect";
import ExchangeConfirmation from "./ExchangeConfirmation";
import DownloadExchangeLabel from "./DownloadExchangeLabel";

const ExchangeLink = () => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const [orderData, setOrderData] = useState(null);
	const [canUpdate, setCanUpdate] = useState(false); //need to wait for flattenedProducts to load
	const [currentIndex, setCurrentIndex] = useState(null); //item currently being viewed in item exchange. This moves back and forth through selectedProducts list
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("loading exchange");
    const [editableFields, setEditableFields] = useState({});
    const [totalCost, setTotalCost] = useState(0);
    const [feedbackChoicesByProduct, setFeedbackChoicesByProduct] = useState(null);
    const [selectedExchangeProducts, setSelectedExchangeProducts] = useState(null); //all products from a given exchange order
    const [exchangeProductData, setExchangeProductData] = useState(null); //Global state for storing data for all variants and variant options for all available exchange products
    const [flattenedProducts, setFlattenedProducts] = useState([]); //Global state for exchange flow data. filtered version of selectedExchangeProducts to individualize products that have quantities greater than 1. 
    const [selectedProducts, setSelectedProducts] = useState([]); //copy of flattenedProducts containing list of just the unique ids selected
    const [newItemsSelectedOptions, setNewItemsSelectedOptions]= useState([]); //store all variant information for each indivdual selectedExchangeProduct's replacement item here
    const [renderExchangeConfirmation, setRenderExchangeConfirmation] = useState(false);
	const [renderItemExchange, setRenderItemExchange] = useState(false);
	const [renderExchangeItemSelect, setRenderExchangeItemSelect] = useState(false);
	const [renderDownloadLabel, setRenderDownloadLabel] = useState(false);
	const [error, setError] = useState(null);
	const [visibleChoices, setVisibleChoices] = useState([]);
    const fontFamilyOne = useFont().scheme_one;
    const location = useLocation();
    const navigate = useNavigate();
	const queryParams = new URLSearchParams(location.search);
	const logoUrl = useLogo();
	const token = queryParams.get("token");
	const getExchangeData = false;
	const expirationStatus = useExpiration(apiUrl, token);
	const sessionId = usePortalSession(token, apiUrl);
	const skuGroups = {"Fulton": [["Ath", "Ins", "Uni"], ["HS", "SL"]]} //If "Ath" is selected for example, send up all skus in the group that belongs to because they are all eligible for an exchange

    //animated elipses loading effect
    useEffect(() => {
        if (!loading && flattenedProducts.length == 0) {
            const interval = startEllipsisInterval(setLoadingText, loadingText);
            return () => clearInterval(interval);
        }
    }, [loading, flattenedProducts]);

    useEffect(() => {
    	if (loading && !orderData) {
    		checkExchangeStatus();
    		fetchReturnData(token, apiUrl, setOrderData, setTotalCost, setError, setEditableFields, getExchangeData);
    		setLoading(false);
    	}
    }, [loading])

    useEffect(() => {
    	if (token) { fetchSelectedExchangeProducts();  }
    }, [token]);	

    //fetch configured exchange products selected by brand from its list of active products on shopify
	useEffect(() => {
		const fetchEligibleExchangeProductData = async () => {
			if (!selectedExchangeProducts || !orderData) { return; }
			const businessSlug = orderData.business_name.toLowerCase();
			const prices = selectedExchangeProducts.map(item => parseFloat(item.product_cost) / (item.product_quantity || 1));
			let finalSkuList = [];
			// get prefixes like ["Ath","HS"]
			if (businessSlug !== "terlingua threads") { //HARDCODE
				const selectedSkus = selectedExchangeProducts.map(item => item.product_sku.split(/[\/-]/)[0]);
				for (const sku of selectedSkus) {
					if (finalSkuList.includes(sku)) continue;
				  	for (const group of skuGroups[orderData.business_name]) {
				    	if (group.includes(sku)) {
					      	for (const gSku of group) {
					        	if (!finalSkuList.includes(gSku)) { finalSkuList.push(gSku); }
					      	}
					      	break; // stop once the matching group is found
				    	}
				  	}
				}
			}
			try {
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/fetch_eligible_exchange_product_data/?token=${encodeURIComponent(token)}`, {
					method: "POST",
					headers: {"Content-Type": "application/json"},
					body: JSON.stringify({ "skus": finalSkuList, "prices": prices })
				});
				const data = await response.json();
				setExchangeProductData(data.eligible_exchange_product_data || []);
			} catch (error) { 
				console.error("Failed to fetch eligible exchange products:", error); 
			}
		};
		fetchEligibleExchangeProductData();
	}, [selectedExchangeProducts, orderData]);

	useEffect(() => {
	    const fetchFeedbackChoices = async () => {
	    	try {
		        const response = await fetch(`${apiUrl}/api/get_multiple_exchange_product_feedback_reasons/`, {
		        	method: "POST",
		          	headers: {"Content-Type": "application/json"},
		          	body: JSON.stringify({ product_ids: exchangeProductData.map(p => p.product_id), token }),
		        });
		        const data = await response.json();
		        setFeedbackChoicesByProduct(data.choices_by_product);
		    } catch (err) { console.error("Error fetching multiple exchange product feedback reasons:", err); }	             
	    };
	    if (exchangeProductData) { fetchFeedbackChoices(); }
	  }, [exchangeProductData]);

    //serialize products into a workable format that we can keep track of exchange data
	// useEffect(() => {
	// 	if (selectedExchangeProducts && exchangeProductData) {
	// 		let uniqueProductId = 1;
	// 		// Flatten products based on quantity (if there is more than 1 for quantity on a given product, add that product to flattenedProducts however many times quantity)
	// 		const expanded = selectedExchangeProducts.flatMap(product => {
	// 			const quantity = parseInt(product.product_quantity || 1, 10);
	// 			const maxQuantity = parseInt(product.max_product_quantity || 1, 10);
	// 			const unitCost = (parseFloat(product.product_cost || "0") / maxQuantity).toFixed(2); // ensure it's a number & formatted
	// 			return Array.from({ length: quantity }, () => ({
	// 				...product,
	// 				product_cost: unitCost,
	// 				product_quantity: 1,
	// 				selected: selectedExchangeProducts.length > 1 || quantity > 1 ? false : true, //if we've begun an exchange for one product, it is automatically selected
	// 				choices: {},
	// 				written_feedback: "",
	// 				unique_id: uniqueProductId++, //for tracking item in exchange. Unrelated to Shopify unique id
	// 			}));
	// 		});
	// 		const allowedTitles = exchangeProductData.map(p => p.product_title); // Get list of eligible product titles from exchangeProductData
	// 		const filteredExpanded = expanded.filter(p => allowedTitles.includes(p.product_title)); // Filter expanded to only include products with matching titles
	// 		setCurrentIndex(0);
	// 		setFlattenedProducts(filteredExpanded);
	// 		setCanUpdate(true);
	// 	}
	// }, [selectedExchangeProducts, exchangeProductData]);

	useEffect(() => {
		if (selectedExchangeProducts && exchangeProductData) {
			let uniqueProductId = 1;
			// Flatten products based on quantity (if there is more than 1 for quantity on a given product, add that product to flattenedProducts however many times quantity)
			const expanded = selectedExchangeProducts.flatMap(product => {
				const quantity = parseInt(product.product_quantity || 1, 10);
				const maxQuantity = parseInt(product.max_product_quantity || 1, 10);
				const unitCost = (parseFloat(product.product_cost || "0") / maxQuantity).toFixed(2); // ensure it's a number & formatted
				return Array.from({ length: quantity }, () => ({
					...product,
					product_cost: unitCost,
					product_quantity: 1,
					selected: selectedExchangeProducts.length > 1 || quantity > 1 ? false : true, //if we've begun an exchange for one product, it is automatically selected
					choices: {},
					written_feedback: "",
					unique_id: uniqueProductId++, //for tracking item in exchange. Unrelated to Shopify unique id
				}));
			});
			const allowedSkus = new Set(exchangeProductData.flatMap(p => p.shopify_variants.map(v => v.sku))); //get unique list of eligible skus from exchangeProductData
			const filteredExpanded = expanded.filter(p => allowedSkus.has(p.product_sku)); // Filter expanded to only include products with matching skus
			setCurrentIndex(0);
			setFlattenedProducts(filteredExpanded);
			setCanUpdate(true);
		}
	}, [selectedExchangeProducts, exchangeProductData]);

	useEffect(() => {
		if (canUpdate) {
			const selectedIds = flattenedProducts.filter(product => product.selected).map(product => product.unique_id);
			if (selectedIds.length < selectedProducts.length) { setCurrentIndex(0); } //reset index whenever products are deselected
			setSelectedProducts(selectedIds); //put just ids of selected products in a list to keep track of currentIndex to toggle back and forth through items in item exchange 
			// if unique_id doesn't exist, add it, otherwise preserve what is there. Use flattenedProducts to find the corresponding object for each id so we can also grab product_title
			setNewItemsSelectedOptions(prev => {
				return [
					...prev,
					...selectedIds
						.filter(id => !prev.some(item => item.unique_id === id))
						.map(id => {
							const product = flattenedProducts.find(p => p.unique_id === id);
							return { unique_id: id, product_title: product.product_title, product_sku: product.product_sku };
						})
				];
			});
		}
	}, [flattenedProducts]);

	//Redirect if trying to access pre-confirmation page after label purchase
    const checkExchangeStatus = async () => {
        try {
            const response = await fetch(`${apiUrl}/api/check_return_status/${token}/`);
            const data = await response.json();      
            if (data.status) { navigate(`/exchanges_confirm/${data.order_id}?token=${token}`); } // If trying to access pre-confirmation page after label download, redirect to post-confirmation                   
        } catch (err) { console.log("error checking exchange status: ", err); }               
    };

    const fetchSelectedExchangeProducts = async () => {
    	try {
        	const response = await fetch(`${apiUrl}/api/fetch_exchange_order_items/?token=${encodeURIComponent(token)}`, {
				method: "GET",
				headers: {"Content-Type": "application/json"}
			});
        	const data = await response.json();
        	//clean exchange products
        	const sorted_data = data.exchange_products.sort((a, b) => a.product_title.localeCompare(b.product_title) ); //sort alphabetically        
			const totalQuantity = sorted_data.reduce((sum, item) => { return sum + (Number(item.product_quantity) || 0); }, 0); // Sum all product_quantity values
			setRenderExchangeItemSelect(totalQuantity > 1);
			setRenderItemExchange(!(totalQuantity > 1));
        	setSelectedExchangeProducts(sorted_data);
    	} catch (err) { 
    		console.log("Failed to fetch exchange products", err); }
	}

	return (
		<>
		{!expirationStatus && (
			<div className={`${styles.exchange_link_container} ${renderExchangeConfirmation ? styles.extend : ""} ${fontFamilyOne}`}>
				<Helmet><title>Start exchange</title></Helmet>
				{logoUrl && (<LogoWrapper class_={styles.exchange_link_logo} src={`${process.env.PUBLIC_URL}/business_images/${logoUrl}`}/>)}
				{!loading && flattenedProducts.length == 0 && (<p className={`${styles.exchange_link_loading_text} fade_in`}>{loadingText}</p>)}
				{!loading && flattenedProducts.length > 0 && (
					<>
						{renderExchangeItemSelect && (<ExchangeItemSelect selectedExchangeProducts={selectedExchangeProducts} visibleChoices={visibleChoices} setVisibleChoices={setVisibleChoices} setRenderExchangeItemSelect={setRenderExchangeItemSelect} setRenderItemExchange={setRenderItemExchange} flattenedProducts={flattenedProducts} setFlattenedProducts={setFlattenedProducts} linkType={"exchange"} setNewItemsSelectedOptions={setNewItemsSelectedOptions} />)}
						{renderItemExchange && feedbackChoicesByProduct && selectedProducts.length > 0 && (<ItemExchange business={editableFields.business_name} selectedExchangeProducts={selectedExchangeProducts} setRenderItemExchange={setRenderItemExchange} setRenderExchangeItemSelect={setRenderExchangeItemSelect} selectedProducts={selectedProducts} flattenedProducts={flattenedProducts} setFlattenedProducts={setFlattenedProducts} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} exchangeProductData={exchangeProductData} newItemsSelectedOptions={newItemsSelectedOptions} setNewItemsSelectedOptions={setNewItemsSelectedOptions} setRenderExchangeConfirmation={setRenderExchangeConfirmation} feedbackChoicesByProduct={feedbackChoicesByProduct} />)}
						{renderExchangeConfirmation && (<ExchangeConfirmation newItemsSelectedOptions={newItemsSelectedOptions} productsBeingExchanged={flattenedProducts.filter(product => selectedProducts.includes(product.unique_id))} setRenderExchangeConfirmation={setRenderExchangeConfirmation} setRenderDownloadLabel={setRenderDownloadLabel} setRenderItemExchange={setRenderItemExchange} />)}
						{renderDownloadLabel && (<DownloadExchangeLabel flattenedProducts={flattenedProducts} exchangeProductData={exchangeProductData} productsBeingReturned={flattenedProducts.filter(obj => selectedProducts.includes(obj.unique_id))} newProductsSelected={newItemsSelectedOptions} setRenderExchangeConfirmation={setRenderExchangeConfirmation} setRenderDownloadLabel={setRenderDownloadLabel} orderData={orderData} token={token} apiUrl={apiUrl} font={fontFamilyOne} />)}
					</>
				)}
			</div>
		)}
		{expirationStatus && (<ExpiredNotice font={fontFamilyOne} linkType={"exchange"} />)}
		</>
	);
};

export default ExchangeLink;